import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CardSeplag from '../../../../components/cardSeplag/CardSeplag';
import { errosTratados } from '../../../../config/Axios';
import Shareds from '../../../../config/translations/Shareds';
import { bindParams, printToast } from '../../../utils/Global';
import { isEmpty } from '../../../utils/ValidForm';
import BaseConfigForm from './BaseConfigForm';

export const isView = () => window.location.href.includes('/view');

export function LayoutBaseForm({
    formik,
    page,
    className = '',
    onBack,
    loadData,
    paramKey = 'id',
    formController,
    initialFormik,
    children,
    enableSalvar = true,
}) {
    const history = useHistory();
    const params = useParams();

    const childrens = children?.length > 1 ? children : [children];

    if (!formController) {
        return <></>;
    }

    return (
        <CardSeplag
            title={page?.title}
            legenda={() => {
                if (isView()) {
                    return Shareds.form.actions.view;
                } else {
                    return isEmpty(params[paramKey])
                        ? Shareds.form.actions.create
                        : Shareds.form.actions.update;
                }
            }}
            config={formController.configs}
            className={className}
        >
            <form onSubmit={formik.handleSubmit} className="p-col-12 p-grid p-fluid">
                {childrens.map((child, index) =>
                    React.cloneElement(child, {
                        ...child.props,
                        key: index,
                        formik,
                        loadData,
                        formController,
                        initialFormik,
                    })
                )}
                <hr />
                <div className="buttonsRight">
                    <Button
                        label="Voltar"
                        icon="pi pi-arrow-left"
                        iconPos="left"
                        className="p-button-secondary"
                        type="button"
                        onClick={() => {
                            const goBack = () =>
                                history.push(`${bindParams(page?.url?.list, params)}`);

                            return onBack ? onBack(formController, goBack) : goBack();
                        }}
                    />

                    {!formController.isView() && (
                        <Button
                            label="Salvar"
                            icon="pi pi-check"
                            iconPos="left"
                            type="submit"
                            className="p-button-info"
                            onClick={() => formController.addCountClick()}
                            disabled={!enableSalvar}
                        />
                    )}
                </div>
            </form>
        </CardSeplag>
    );
}

function BaseForm(
    {
        initialValues,
        paramKey = 'id',
        page,
        service,
        children,
        onValidate,
        validateOnChange = true,
        buscarOnLoad = true,
        onSubmit,
        onBuscar,
        createDependences,
        actionOnSave = ({ success }) => success,
        actionOnCreate = (formik) => formik,
        prepareInitialValues = async (response) => response,
        showToast = true,
        ignoreInputsValidate = [],
        redirectOnSave = true,
        ...rest
    },
    ref
) {
    const params = useParams();
    const [formController, setFormController] = useState(null);
    const [initialFormik, setInitialFormik] = useState(null);
    if (formController) {
        formController.setId(params[paramKey]);
    }
    const history = useHistory();
    const formik = useFormik({
        initialValues: null,
        validateOnChange,
        validate: (data) => {
            if (onValidate && formController) {
                return onValidate({ formController, data, errors: formController.validate(data) });
            }

            if (formController) {
                return formController.validate(data);
            }
            return {};
        },
        onSubmit: async (value, actions) => saveForm(value, actions),
    });

    const setInitialValues = async (initial) => {
        const form = new BaseConfigForm(initialValues);
        form.setInitialValues({ ...form.initialValues, ...initial });
        formik.setValues({ ...form.initialValues, ...initial });
        setInitialFormik({ afterBaseFormLoaded: true, ...form.initialValues, ...initial });
        setFormController(form);
    };

    const saveForm = async (value, formik) => {
        try {
            const response = await (onSubmit
                ? onSubmit({ registro: value, formik, formController, params })
                : service.salvar({ registro: value, formik, formController, params }));
            formController.setCountClick(0);
            actionOnSave({ success: true, response, onLoad });

            if (showToast) {
                printToast({
                    severity: 'success',
                    summary: page?.title ?? '',
                    detail: formController.isNew()
                        ? Shareds.form.successCreate
                        : Shareds.form.successUpdate,
                });
            }

            if (redirectOnSave) {
                onGoBack();
            }

            return { success: true, response };
        } catch (error) {
            actionOnSave({ success: false, response: error });
            if (Array.isArray(error?.response?.data?.errors)) {
                error?.response?.data?.errors.forEach((error) => {
                    formik.setFieldError(error.field, error.message);
                });
            } else {
                formController.setCountClick(0);
                if (error?.response && !errosTratados.includes(error?.response?.status)) {
                    printToast({
                        severity: 'error',
                        summary: page?.title ?? '',
                        detail: error?.response,
                    });
                }
            }
            return { success: false, error };
        }
    };

    const onLoad = async (onRefresh = null) => {
        try {
            const form = new BaseConfigForm(initialValues);
            const response = await (onBuscar ? onBuscar(form) : service.buscarPorId(params));
            const res = await (onBuscar ? response : service.beforeSelectOnAlter(response, form));
            const initial = await prepareInitialValues(res);
            form.setInitialValues(initial);
            formik.setValues({ ...form.initialValues, ...initial });
            setInitialFormik({ afterBaseFormLoaded: true, ...form.initialValues, ...initial });
            setFormController(form);
            if (onRefresh) {
                onRefresh(formik);
            }
        } catch (error) {
            if (!error?.response) {
                printToast({
                    severity: 'error',
                    summary: page?.title ?? '',
                    detail: error?.message,
                });
            }
        }
    };

    useEffect(() => {
        const init = async () => {
            if (formController) {
                formController.setCountClick(0);
            }
            actionOnCreate(formik);
            const form = new BaseConfigForm(initialValues);
            if (isEmpty(params?.[paramKey]) || (!isEmpty(params?.[paramKey]) && !buscarOnLoad)) {
                const initial = await prepareInitialValues(form.initialValues);
                formik.setValues(initial);
                form.setInitialValues(initial);
                setInitialFormik({ afterBaseFormLoaded: true, ...initial });
                setFormController(form);
            } else if (!isEmpty(params[paramKey]) && buscarOnLoad) {
                onLoad();
            } else if (!isEmpty(params?.[paramKey])) {
                formik.setValues(form.initialValues);
                setFormController(form);
            }
        };
        init();
    }, [createDependences]);

    const onGoBack = () => {
        history.push(`${bindParams(page?.url?.list, params)}`);
    };

    const verificaAlteracao = () => {
        const { hasChange } = BaseConfigForm.hasChanged(
            initialFormik,
            formik.values,
            formController.inputs,
            ignoreInputsValidate
        );
        return hasChange;
    };
    if (formController) {
        formController.hasChangeInputs = verificaAlteracao();
    }

    useImperativeHandle(
        ref,
        () => {
            return {
                onLoad,
                formik,
                verificaAlteracao,
                saveForm,
                formController,
                setInitialValues,
            };
        },
        [formik.values, initialFormik, formController]
    );
    const childrens = children?.length > 1 ? children : [children];

    if (!formController) return <></>;

    return childrens.map((child, index) =>
        React.cloneElement(child, {
            ...rest,
            paramKey,
            ...child.props,
            key: index,
            formController,
            page,
            formik,
            onLoad,
            initialFormik,
        })
    );
}
export default forwardRef(BaseForm);
